import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Paper, Typography, Chip, IconButton, Tooltip, CircularProgress, Alert,
  FormGroup, FormControlLabel, Checkbox, Divider, Stack, ToggleButton,
  ToggleButtonGroup, List, ListItemButton, ListItemText,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CytoscapeComponent from 'react-cytoscapejs';
import cytoscape from 'cytoscape';
import fcose from 'cytoscape-fcose';

import api from '../../services/api';

if (!cytoscape.__fcoseRegistered) {
  try { cytoscape.use(fcose); } catch (e) { /* já registrado */ }
  cytoscape.__fcoseRegistered = true;
}

// Cores por fabricante (igual ao espírito do anubis)
const VENDOR_COLOR = {
  HUAWEI: '#c0392b', MIKROTIK: '#2980b9', VSOL: '#27ae60', JUNIPER: '#8e44ad',
  CISCO: '#16a085', DATACOM: '#2c3e50', A10: '#d35400', FIBERHOME: '#f39c12',
  NOKIA: '#7f8c8d', UBIQUITI: '#1abc9c', ZTE: '#9b59b6', PARKS: '#e67e22',
  FURUKAWA: '#95a5a6', CIANET: '#34495e', CHIMA: '#7f8c8d', ZYXEL: '#b2bec3',
  EXTERNAL: '#90a4ae',
};
const vendorColor = (v) => VENDOR_COLOR[(v || '').toUpperCase()] || '#546e7a';

// Forma por papel no grafo
const ROLE_SHAPE = {
  border: 'hexagon', core: 'diamond', olt: 'round-octagon',
  dist: 'round-rectangle', access: 'ellipse', external: 'round-tag',
};
const ROLE_LABEL = {
  border: 'Borda (eBGP)', core: 'Núcleo (OSPF área 0)', dist: 'Distribuição',
  olt: 'OLT', access: 'Acesso/Rádio', external: 'AS externo',
};

// Ícones SVG (brancos) por papel — sobrepostos à cor do fabricante
const svg = (inner) => 'data:image/svg+xml;utf8,' + encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${inner}</svg>`);
const ICONS = {
  router: svg('<circle cx="12" cy="12" r="3"/><path d="M12 2v4M12 18v4M2 12h4M18 12h4M5 5l3 3M16 16l3 3M19 5l-3 3M8 16l-3 3"/>'),
  switch: svg('<rect x="2" y="7" width="20" height="10" rx="2"/><path d="M6 11h2M11 11h2M16 11h2"/>'),
  olt: svg('<rect x="3" y="4" width="18" height="16" rx="2"/><path d="M7 8h10M7 12h10M7 16h6"/>'),
  radio: svg('<path d="M12 12v9"/><path d="M7.8 7.8a6 6 0 0 0 0 6.4M16.2 7.8a6 6 0 0 1 0 6.4M5 5a9 9 0 0 0 0 10M19 5a9 9 0 0 1 0 10"/>'),
  cloud: svg('<path d="M17 18a4 4 0 0 0 0-8 6 6 0 0 0-11.5 2A3.5 3.5 0 0 0 6 18z"/>'),
};
const ICON_BY_ROLE = { border: 'router', core: 'router', dist: 'switch', olt: 'olt', access: 'radio', external: 'cloud' };

// Protocolos/enlaces -> cor (legenda + estilo)
const PROTOCOLS = [
  { key: 'bgp',        label: 'BGP',        color: '#e67e22' },
  { key: 'ospf_adj',   label: 'OSPF',       color: '#9b59b6' },
  { key: 'p2p',        label: 'p2p /30',    color: '#3498db' },
  { key: 'shared_lan', label: 'LAN',        color: '#16a085' },
  { key: 'trunk',      label: 'Trunk/VLAN', color: '#2ecc71' },
];

const SEV_COLOR = { high: '#e01b29', medium: '#f39c12', low: '#90a4ae' };

// Presets de modo (toolbar) -> camadas ligadas
const MODES = {
  tudo:      { L1: true,  L2: true,  L3: true,  ISSUES: true },
  L1:        { L1: true,  L2: false, L3: false, ISSUES: false },
  L2:        { L1: false, L2: true,  L3: false, ISSUES: false },
  L3:        { L1: false, L2: false, L3: true,  ISSUES: false },
  problemas: { L1: true,  L2: false, L3: true,  ISSUES: true },
};

const STYLE = [
  { selector: 'node', style: {
    'label': 'data(name)', 'background-color': 'data(color)', 'shape': 'data(shape)',
    'background-image': 'data(icon)', 'background-fit': 'none',
    'background-width': '52%', 'background-height': '52%', 'background-clip': 'none',
    'color': '#37474f', 'text-valign': 'bottom', 'text-halign': 'center', 'text-margin-y': 3,
    'width': 48, 'height': 48, 'font-size': 9, 'text-wrap': 'wrap', 'text-max-width': 90,
    'border-width': 0,
  } },
  { selector: 'node[?is_virtual]', style: { 'border-width': 2, 'border-style': 'dashed', 'border-color': '#e67e22', 'background-opacity': 0.55 } },
  { selector: 'node.issue', style: { 'border-width': 4, 'border-color': '#e01b29', 'border-style': 'solid' } },
  { selector: 'node.focus', style: { 'border-width': 5, 'border-color': '#ffd600', 'border-style': 'solid' } },
  { selector: '.dim', style: { 'opacity': 0.12 } },
  { selector: 'edge', style: {
    'label': 'data(label)', 'width': 2, 'line-color': '#b0bec5', 'font-size': 7,
    'color': '#607d8b', 'curve-style': 'bezier', 'text-rotation': 'autorotate',
    'text-background-color': '#fff', 'text-background-opacity': 0.7, 'text-background-padding': 1,
  } },
  { selector: 'edge[kind = "bgp"]',        style: { 'line-color': '#e67e22', 'width': 3 } },
  { selector: 'edge[kind = "ospf_adj"]',   style: { 'line-color': '#9b59b6', 'width': 2.5 } },
  { selector: 'edge[kind = "p2p"]',        style: { 'line-color': '#3498db' } },
  { selector: 'edge[kind = "shared_lan"]', style: { 'line-color': '#16a085', 'line-style': 'dashed' } },
  { selector: 'edge[kind = "trunk"]',      style: { 'line-color': '#2ecc71', 'line-style': 'dashed', 'width': 2.5 } },
];

const LAYOUT = {
  name: 'concentric', concentric: (n) => n.data('rank') || 1,
  levelWidth: () => 1, minNodeSpacing: 55, spacingFactor: 1.05, animate: true,
};

export default function Inventory() {
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [layerDefs, setLayerDefs] = useState({});
  const [issues, setIssues] = useState([]);
  const [active, setActive] = useState({ L1: true, L2: false, L3: true, ISSUES: true });
  const [mode, setMode] = useState('tudo');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const cyRef = useRef(null);

  const load = () => {
    setLoading(true);
    setErr(null);
    api.get('/hermes/inventory/topology')
      .then((r) => {
        const { nodes: ns = [], edges: es = [], layers = {}, issues: iss = [], stats: s } = r.data || {};
        const decorated = ns.map((n) => ({ data: {
          ...n.data,
          color: vendorColor(n.data.vendor),
          shape: ROLE_SHAPE[n.data.role] || 'ellipse',
          icon: ICONS[ICON_BY_ROLE[n.data.role] || 'router'],
        } }));
        setNodes(decorated);
        setEdges(es);
        setLayerDefs(layers);
        setIssues(iss);
        setStats(s);
      })
      .catch(() => setErr('Falha ao carregar a topologia.'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  // aplica filtro de camadas + overlay/realce de problemas
  const applyView = () => {
    const cy = cyRef.current;
    if (!cy) return;
    const on = Object.entries(active).filter(([k, v]) => v && k !== 'ISSUES').map(([k]) => k);
    const focusMode = mode === 'problemas';
    cy.batch(() => {
      cy.edges().forEach((e) => {
        const ls = e.data('layers') || [];
        e.style('display', ls.some((l) => on.includes(l)) ? 'element' : 'none');
        const issue = e.source().data('has_issues') || e.target().data('has_issues');
        e.toggleClass('dim', focusMode && !issue);
      });
      cy.nodes().forEach((n) => {
        n.toggleClass('issue', !!active.ISSUES && !!n.data('has_issues'));
        n.toggleClass('dim', focusMode && !n.data('has_issues'));
      });
    });
  };

  useEffect(() => {
    const cy = cyRef.current;
    if (cy && (nodes.length || edges.length)) {
      try { cy.layout(LAYOUT).run(); applyView(); } catch (e) { /* ignore */ }
    }
  }, [nodes, edges]);
  useEffect(applyView, [active, mode]);

  const pickMode = (_e, m) => { if (!m) return; setMode(m); setActive((a) => ({ ...a, ...MODES[m] })); };
  const toggle = (k) => { setMode('custom'); setActive((a) => ({ ...a, [k]: !a[k] })); };
  const focusHosts = (ids) => {
    const cy = cyRef.current;
    if (!cy) return;
    cy.nodes().removeClass('focus');
    (ids || []).forEach((id) => cy.getElementById(String(id)).addClass('focus'));
    const eles = cy.collection((ids || []).map((id) => cy.getElementById(String(id))));
    if (eles.nonempty()) cy.animate({ fit: { eles, padding: 80 } }, { duration: 350 });
  };

  const elements = [...nodes, ...edges];
  const rolesPresent = [...new Set(nodes.map((n) => n.data.role))].filter(Boolean);

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Inventário da Rede
            <Chip label="BETA" size="small" color="secondary" sx={{ ml: 1, verticalAlign: 'middle' }} />
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Topologia em camadas (L1 Físico / L2 VLAN / L3 Roteamento) derivada dos backups.
          </Typography>
        </Box>
        <Tooltip title="Recarregar"><IconButton onClick={load}><RefreshIcon /></IconButton></Tooltip>
      </Box>

      {stats && (
        <Box mb={1} display="flex" gap={1} flexWrap="wrap" alignItems="center">
          <Chip size="small" label={`${stats.hosts} hosts`} />
          <Chip size="small" label={`${stats.hosts_parsed} com config lido`} />
          <Chip size="small" label={`${stats.links} enlaces`} color="primary" variant="outlined" />
          {stats.external_as > 0 && <Chip size="small" label={`${stats.external_as} AS externos`} />}
          {stats.issues > 0 && <Chip size="small" color="error" label={`${stats.issues} problemas`} />}
          <Box flexGrow={1} />
          <ToggleButtonGroup size="small" exclusive value={mode} onChange={pickMode}>
            <ToggleButton value="tudo">Tudo</ToggleButton>
            <ToggleButton value="L1">L1</ToggleButton>
            <ToggleButton value="L2">L2</ToggleButton>
            <ToggleButton value="L3">L3</ToggleButton>
            <ToggleButton value="problemas">Problemas</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}
      {err && <Alert severity="error" sx={{ mb: 1 }}>{err}</Alert>}

      <Paper variant="outlined" sx={{ height: '72vh', position: 'relative', overflow: 'hidden' }}>
        {/* painel de camadas + legenda */}
        <Paper elevation={3} sx={{
          position: 'absolute', top: 10, left: 10, zIndex: 5, p: 1.2, width: 200,
          maxHeight: 'calc(72vh - 20px)', overflow: 'auto', bgcolor: 'rgba(255,255,255,.96)',
        }}>
          <Typography variant="overline" color="text.secondary">Camadas</Typography>
          <FormGroup>
            {Object.entries(layerDefs).map(([k, def]) => (
              <FormControlLabel key={k} sx={{ my: -0.4 }}
                control={<Checkbox size="small" checked={!!active[k]} onChange={() => toggle(k)} />}
                label={<Typography variant="body2">{def.label}</Typography>} />
            ))}
          </FormGroup>
          <Divider sx={{ my: 0.8 }} />
          <Typography variant="overline" color="text.secondary">Enlaces</Typography>
          <Stack spacing={0.3} sx={{ mb: 0.8 }}>
            {PROTOCOLS.map((p) => (
              <Box key={p.key} display="flex" alignItems="center" gap={0.8}>
                <Box sx={{ width: 16, height: 3, bgcolor: p.color, borderRadius: 1 }} />
                <Typography variant="caption">{p.label}</Typography>
              </Box>
            ))}
          </Stack>
          {rolesPresent.length > 0 && (
            <>
              <Divider sx={{ my: 0.8 }} />
              <Typography variant="overline" color="text.secondary">Papéis</Typography>
              <Stack spacing={0.2}>
                {rolesPresent.map((r) => (
                  <Typography key={r} variant="caption" color="text.secondary">• {ROLE_LABEL[r] || r}</Typography>
                ))}
              </Stack>
            </>
          )}
        </Paper>

        {/* painel de problemas */}
        {issues.length > 0 && (
          <Paper elevation={3} sx={{
            position: 'absolute', top: 10, right: 10, zIndex: 5, p: 1, width: 250,
            maxHeight: 'calc(72vh - 20px)', overflow: 'auto', bgcolor: 'rgba(255,255,255,.96)',
          }}>
            <Typography variant="overline" color="text.secondary">Problemas ({issues.length})</Typography>
            <List dense disablePadding>
              {issues.map((p, i) => (
                <ListItemButton key={i} sx={{ py: 0.2, alignItems: 'flex-start' }} onClick={() => focusHosts(p.hosts)}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', mt: 0.7, mr: 1,
                             bgcolor: SEV_COLOR[p.severity] || '#90a4ae', flexShrink: 0 }} />
                  <ListItemText
                    primary={<Typography variant="caption" fontWeight={600}>{p.kind.replace(/_/g, ' ')}</Typography>}
                    secondary={<Typography variant="caption" color="text.secondary">{p.detail}</Typography>} />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        )}

        {loading && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        )}
        {!loading && elements.length === 0 && !err && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              Sem topologia ainda — rode os backups dos hosts (com IPs/máscaras nas interfaces) ou ligue o modo demo.
            </Typography>
          </Box>
        )}
        <CytoscapeComponent
          elements={elements}
          style={{ width: '100%', height: '100%' }}
          stylesheet={STYLE}
          cy={(cy) => { cyRef.current = cy; }}
          layout={LAYOUT}
        />
      </Paper>
    </Box>
  );
}
